import { BOOKING_APPROVALS, BOOKING_MODES, BOOKING_PAYMENTS, BOOKING_QUOTA_PERIODS, BOOKING_STYLES } from './booking';
import { patchOf } from './patch';
import { z } from 'zod';
import { isValidDocument, onlyDigits } from './documents';
import { MEMBER_ROLES } from './permissions';

const money = z.coerce.number().int('Valor em centavos deve ser inteiro.').min(0, 'Valor não pode ser negativo.');
const optionalText = (max: number) => z.string().trim().max(max).optional().nullable();

export const documentSchema = z
  .string()
  .transform(onlyDigits)
  .refine(isValidDocument, 'CPF ou CNPJ inválido.');

export const TAX_REGIMES = ['MEI', 'SIMPLES_NACIONAL', 'LUCRO_PRESUMIDO', 'LUCRO_REAL'] as const;
export const BUSINESS_TYPES = ['PRODUCTS', 'SERVICES', 'BOTH'] as const;

// ---------- Negócio ----------
export const createTenantSchema = z.object({
  tradeName: z.string().trim().min(2, 'Informe o nome do negócio.').max(80),
  legalName: optionalText(120),
  document: documentSchema.optional().nullable(),
  businessType: z.enum(BUSINESS_TYPES),
  taxRegime: z.enum(TAX_REGIMES).optional().nullable(),
  /** Plano escolhido no site (trial de 14 dias). Sem módulos: trial com todos. */
  plan: z
    .object({
      modules: z.array(z.string()).max(20).default([]),
      interval: z.enum(['MONTHLY', 'YEARLY']).default('MONTHLY'),
      bundleKey: z.string().max(40).optional().nullable(),
    })
    .optional(),
});
export type CreateTenantInput = z.infer<typeof createTenantSchema>;

export const notificationPreferenceSchema = z.object({
  panel: z.boolean().default(true),
  email: z.boolean().default(true),
  whatsapp: z.boolean().default(false),
});

export const tenantSettingsSchema = z
  .object({
    logoKey: z.string().max(300).optional(),
    primaryColor: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Cor inválida.').optional(),
    secondaryColor: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Cor inválida.').optional(),
    timezone: z.string().default('America/Sao_Paulo').optional(),
    ownerWhatsapp: z.string().max(20).optional(),
    accountantEmail: z.string().email('E-mail inválido.').optional(),
    notifications: z.record(z.string(), notificationPreferenceSchema).optional(),
  })
  .strict();

export const updateTenantSchema = z.object({
  tradeName: z.string().trim().min(2).max(80).optional(),
  legalName: optionalText(120),
  document: documentSchema.optional().nullable(),
  businessType: z.enum(BUSINESS_TYPES).optional(),
  taxRegime: z.enum(TAX_REGIMES).optional().nullable(),
  settings: patchOf(tenantSettingsSchema).optional(),
});
export type UpdateTenantInput = z.infer<typeof updateTenantSchema>;

export const uploadRequestSchema = z.object({
  contentType: z.string().regex(/^image\/(png|jpeg|webp|gif|avif)$/, 'Envie uma imagem (PNG, JPG, WebP, GIF ou AVIF).'),
  size: z.coerce.number().int().positive().max(10 * 1024 * 1024, 'A imagem pode ter no máximo 10 MB.'),
});

// ---------- Catálogo ----------
export const PRODUCT_STATUSES = ['DRAFT', 'ACTIVE', 'ARCHIVED'] as const;
/** Recorrências de assinatura: cobrança todo mês ou toda semana. */
export const SUBSCRIPTION_INTERVALS = ['MONTHLY', 'WEEKLY'] as const;
export type SubscriptionInterval = (typeof SUBSCRIPTION_INTERVALS)[number];

export const variantInputSchema = z.object({
  id: z.string().uuid().optional(),
  sku: z.string().trim().min(1).max(60).optional(),
  name: z.string().trim().max(120).optional(),
  options: z.record(z.string(), z.string()).default({}),
  priceCents: money,
  compareAtCents: money.optional().nullable(),
  costCents: money.optional().nullable(),
  barcode: optionalText(60),
  weightGrams: z.coerce.number().int().min(0).optional().nullable(),
  heightCm: z.coerce.number().int().min(0).optional().nullable(),
  widthCm: z.coerce.number().int().min(0).optional().nullable(),
  lengthCm: z.coerce.number().int().min(0).optional().nullable(),
  trackInventory: z.boolean().optional(),
});
export type VariantInput = z.infer<typeof variantInputSchema>;

const productFields = {
  type: z.enum(['PRODUCT', 'SERVICE']).default('PRODUCT'),
  status: z.enum(PRODUCT_STATUSES).default('DRAFT'),
  name: z.string().trim().min(2, 'Informe o nome do produto.').max(150),
  slug: z
    .string()
    .trim()
    .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'Use só letras minúsculas, números e hífen.')
    .max(80)
    .optional(),
  description: optionalText(10_000),
  categoryId: z.string().uuid().optional().nullable(),
  featured: z.boolean().optional(),
  ncm: z.string().regex(/^\d{8}$/, 'O NCM tem 8 dígitos.').optional().nullable(),
  cest: z.string().regex(/^\d{7}$/, 'O CEST tem 7 dígitos.').optional().nullable(),
  origin: z.coerce.number().int().min(0).max(8).optional().nullable(),
  unit: z.string().trim().max(6).optional(),
  serviceCode: optionalText(20),
  taxProfileId: z.string().uuid().optional().nullable(),
  /** Preenchido = serviço vendido por assinatura na loja, cobrado automaticamente a cada ciclo. */
  subscriptionInterval: z.enum(SUBSCRIPTION_INTERVALS).optional().nullable(),
  /** Preenchido = serviço agendado pela agenda online da loja (por horário ou o dia todo). */
  bookingMode: z.enum(BOOKING_MODES).optional().nullable(),
  /** Duração de cada atendimento nos serviços por horário. */
  bookingDurationMinutes: z.coerce.number().int().min(5, 'Mínimo de 5 minutos.').max(720, 'Máximo de 12 horas.').optional().nullable(),
  /** Assinatura com agendamento: quantos atendimentos o cliente tem em cada período. */
  bookingQuota: z.coerce.number().int().min(1, 'Mínimo de 1 atendimento.').max(31, 'Máximo de 31 atendimentos.').optional().nullable(),
  bookingQuotaPeriod: z.enum(BOOKING_QUOTA_PERIODS).optional().nullable(),
  /** Como o assinante pode marcar: horário fixo, escolher cada vez, ou os dois (ele decide na compra). */
  bookingStyles: z.array(z.enum(BOOKING_STYLES)).min(1, 'Escolha pelo menos uma forma de marcar.').max(2).optional().nullable(),
  /** Quando o cliente paga o agendamento feito na loja: no atendimento ou ao marcar (Pix). */
  bookingPayment: z.enum(BOOKING_PAYMENTS).optional(),
  /** Se o agendamento feito na loja já sai confirmado ou espera o dono confirmar. */
  bookingApproval: z.enum(BOOKING_APPROVALS).optional(),
  seo: z.object({ title: z.string().max(70).optional(), description: z.string().max(160).optional() }).optional(),
};

export const createProductSchema = z.object({
  ...productFields,
  /** Sem variantes: informe o preço e é criada a variante padrão. */
  priceCents: money.optional(),
  costCents: money.optional().nullable(),
  sku: z.string().trim().max(60).optional(),
  variants: z.array(variantInputSchema).max(200).optional(),
});
export type CreateProductInput = z.infer<typeof createProductSchema>;

export const updateProductSchema = patchOf(z.object(productFields)).extend({
  variants: z.array(variantInputSchema).max(200).optional(),
});
export type UpdateProductInput = z.infer<typeof updateProductSchema>;

export const listProductsQuerySchema = z.object({
  q: z.string().trim().max(100).optional(),
  status: z.enum(PRODUCT_STATUSES).optional(),
  categoryId: z.string().uuid().optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});

export const generateVariantsSchema = z.object({
  options: z
    .record(z.string().trim().min(1), z.array(z.string().trim().min(1)).min(1).max(30))
    .refine((o) => Object.keys(o).length >= 1 && Object.keys(o).length <= 3, 'Use de 1 a 3 opções.'),
  priceCents: money,
  costCents: money.optional().nullable(),
});

export const confirmImageSchema = z.object({
  key: z.string().min(5).max(300),
  alt: optionalText(150),
  variantId: z.string().uuid().optional().nullable(),
});

export const importProductsSchema = z.object({
  fileKey: z.string().min(5).max(300),
  format: z.enum(['csv', 'xlsx']),
});

export const categorySchema = z.object({
  name: z.string().trim().min(2).max(60),
  parentId: z.string().uuid().optional().nullable(),
  position: z.coerce.number().int().min(0).optional(),
});

// ---------- Clientes ----------
export const addressSchema = z.object({
  zipCode: z.string().transform(onlyDigits).refine((v) => v.length === 8, 'CEP inválido.'),
  street: z.string().trim().min(2).max(120),
  number: z.string().trim().min(1).max(20),
  complement: optionalText(60),
  district: z.string().trim().min(2).max(80),
  city: z.string().trim().min(2).max(80),
  state: z.string().trim().length(2, 'UF com 2 letras.').transform((s) => s.toUpperCase()),
  ibgeCode: z.string().regex(/^\d{7}$/).optional().nullable(),
  isDefault: z.boolean().optional(),
});
export type AddressInput = z.infer<typeof addressSchema>;

export const customerSchema = z.object({
  name: z.string().trim().min(2, 'Informe o nome.').max(120),
  email: z.string().trim().toLowerCase().email('E-mail inválido.').optional().nullable(),
  phone: z.string().trim().max(25).optional().nullable(),
  document: documentSchema.optional().nullable(),
  tags: z.array(z.string().trim().min(1).max(30)).max(20).optional(),
  marketingOptIn: z.boolean().optional(),
  source: z.enum(['storefront', 'whatsapp', 'manual', 'ads', 'webchat', 'import']).optional(),
  addresses: z.array(addressSchema).max(10).optional(),
});
export type CustomerInput = z.infer<typeof customerSchema>;

export const listCustomersQuerySchema = z.object({
  q: z.string().trim().max(100).optional(),
  tag: z.string().trim().max(30).optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});

export const mergeCustomersSchema = z.object({
  intoId: z.string().uuid(),
  fromIds: z.array(z.string().uuid()).min(1).max(20),
});

// ---------- Equipe ----------
export const inviteMemberSchema = z.object({
  email: z.string().trim().toLowerCase().email('E-mail inválido.'),
  role: z.enum(MEMBER_ROLES).refine((r) => r !== 'OWNER', 'O convite não pode ser de dono.'),
});
export const updateMemberSchema = z.object({ role: z.enum(MEMBER_ROLES) });
export const acceptInviteSchema = z.object({ token: z.string().min(10) });

// ---------- Venda rápida ----------
export const quickSaleSchema = z.object({
  items: z
    .array(
      z.object({
        variantId: z.string().uuid(),
        quantity: z.coerce.number().int().min(1),
        unitPriceCents: money.optional(),
      }),
    )
    .min(1, 'Inclua ao menos um item.'),
  customerId: z.string().uuid().optional().nullable(),
  customer: customerSchema.pick({ name: true, phone: true, email: true }).optional(),
  method: z.enum(['PIX', 'CREDIT_CARD', 'BOLETO', 'CASH', 'OTHER']),
  feeCents: money.default(0),
  notes: optionalText(500),
});
export type QuickSaleRequest = z.infer<typeof quickSaleSchema>;

export const dashboardQuerySchema = z.object({ range: z.enum(['today', 'week', 'month']).default('month') });
